import { Trans } from '@lingui/macro'
import AddIcon from '@mui/icons-material/Add'
import { Box, Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Skeleton, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { FrameworkComponentProps } from '@om1/platform-utils'
import { useEffect, useState } from 'react'
import { CohortTreeActionType, QueryFilterBase, cohortBlocksEditActions } from '../../../state'

interface NetworkSelection {
    selectedNetwork: string
}

export type NetworkDialogProps = FrameworkComponentProps<
    {
        networkDatasets: Array<{ id: string; name: string; columnName: string }>
        datasetsLoading: boolean
    },
    typeof cohortBlocksEditActions,
    {
        action: CohortTreeActionType
        initialValue: QueryFilterBase[]
        onSave: (filters: QueryFilterBase[]) => void
        onCancel: () => void
    }
>

/**
 * A dialog for editing the Network criterion type.
 */
export const NetworkDialogComponent = ({ state, props, actions }: NetworkDialogProps) => {
    const { networkDatasets, datasetsLoading } = state
    const { initialValue, onSave, onCancel } = props

    // Load datasets when dialog opens
    useEffect(() => {
        if (networkDatasets.length === 0 && !datasetsLoading) {
            // @ts-ignore - getDatasets should be available from datasetsActions
            actions.getDatasets()
        }
    }, [actions, networkDatasets.length, datasetsLoading])

    const [formValue, setFormValue] = useState<NetworkSelection>(() => {
        // Parse initial value to get selected network
        const networkFilters = initialValue.filter((filter) => filter.field.includes('IS_IN_') && filter.table === 'patient')

        let selectedNetwork = ''

        if (networkFilters.length > 0) {
            // Extract network ID from the first filter
            const firstFilter = networkFilters[0]
            if (firstFilter.field.includes('IS_IN_')) {
                selectedNetwork = firstFilter.field.replace('IS_IN_', '')
            }
        }

        return {
            selectedNetwork
        }
    })

    const handleNetworkChange = (event: SelectChangeEvent<string>) => {
        setFormValue((prev) => ({
            ...prev,
            selectedNetwork: event.target.value
        }))
    }

    const handleSave = () => {
        if (!formValue.selectedNetwork) {
            onSave([])
            return
        }

        const filters: QueryFilterBase[] = [
            {
                table: 'patient',
                field: `IS_IN_${formValue.selectedNetwork}`,
                operator: 'in',
                values: ['True'] // eslint-disable-line string-to-lingui/missing-lingui-transformation
            }
        ]

        onSave(filters)
    }

    if (datasetsLoading) {
        return (
            <Dialog open maxWidth='sm' fullWidth>
                <DialogTitle>
                    <Trans>Network Selection</Trans>
                </DialogTitle>
                <DialogContent>
                    <Skeleton variant='rectangular' height={200} />
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Dialog open maxWidth='sm' fullWidth>
            <DialogTitle>
                <Trans>Network Selection</Trans>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Typography variant='h6' gutterBottom>
                        <Trans>Select Network</Trans>
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel id='network-select-label'>
                            <Trans>Choose a Network</Trans>
                        </InputLabel>
                        <Select
                            labelId='network-select-label'
                            id='network-select'
                            value={formValue.selectedNetwork}
                            label={<Trans>Choose a Network</Trans>}
                            onChange={handleNetworkChange}
                        >
                            {networkDatasets.map((dataset) => (
                                <MenuItem key={dataset.id} value={dataset.columnName}>
                                    {dataset.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button onClick={handleSave} variant='contained' startIcon={<AddIcon />}>
                    <Trans>Save Criteria</Trans>
                </Button>
            </DialogActions>
        </Dialog>
    )
}
